import React, { useState, useEffect } from 'react';
import UploadPanel from './components/UploadPanel';
import SlideThumbnailList from './components/SlideThumbnailList';
import DeckPreview from './components/DeckPreview';
import ChatPanel from './components/ChatPanel';
import DownloadButton from './components/DownloadButton';
import { isAuthenticated, login, handleCallback, logout, authFetch } from './auth';
import './App.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function App() {
  const [authReady, setAuthReady] = useState(false);
  const [deckId, setDeckId] = useState(null);
  const [deck, setDeck] = useState(null);
  const [previewImages, setPreviewImages] = useState([]);
  const [activeSlide, setActiveSlide] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      if (window.location.search.includes('code=')) {
        await handleCallback();
      }
      if (!isAuthenticated()) {
        login();
        return;
      }
      setAuthReady(true);
    })();
  }, []);

  if (!authReady) return <div className="app"><p>Authenticating...</p></div>;

  const handleGenerate = async (files, text) => {
    setLoading(true);
    setError('');
    const formData = new FormData();
    if (files && files.length > 0) {
      files.forEach(f => formData.append('files', f));
    } else {
      formData.append('text', text);
    }

    try {
      const res = await authFetch(`${API}/generate`, { method: 'POST', body: formData });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || `Failed (${res.status})`); }
      const text_response = await res.text();
      const data = JSON.parse(text_response.trim());
      setDeckId(data.deck_id);
      setPreviewImages(data.preview_images || []);
      // Fetch deck JSON
      const deckRes = await authFetch(`${API}/deck/${data.deck_id}`);
      const deckData = await deckRes.json();
      setDeck(deckData);
      setActiveSlide(0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeckUpdated = async (newDeck) => {
    setDeck(newDeck);
    setActiveSlide(0);
    // Refresh preview
    try {
      const res = await authFetch(`${API}/preview/${deckId}`);
      if (res.ok) {
        const data = await res.json();
        setPreviewImages(data.preview_images || []);
      }
    } catch (e) { /* ignore preview refresh errors */ }
  };

  const handleNewDeck = () => {
    setDeckId(null);
    setDeck(null);
    setPreviewImages([]);
    setActiveSlide(0);
    setError('');
  };

  // Upload view
  if (!deckId) {
    return (
      <div className="app">
        <header className="app-header">
          <h1 className="app-title">Flair Airlines PowerPoint Generator</h1>
          <p className="app-subtitle">Generate executive presentations powered by Amazon Bedrock</p>
        </header>
        <main className="app-main">
          <UploadPanel onGenerate={handleGenerate} loading={loading} />
          {loading && <div className="progress"><div className="progress__bar" /></div>}
          {error && <p className="error-message">⚠ {error}</p>}
        </main>
      </div>
    );
  }

  // Editor view
  return (
    <div className="app app--editor">
      <header className="editor-header">
        <h1 className="editor-header__title">Flair Airlines PowerPoint Generator</h1>
        <div className="editor-header__actions">
          <DownloadButton deckId={deckId} />
          <button className="btn-secondary" onClick={handleNewDeck}>New Deck</button>
          <button className="btn-secondary" onClick={logout}>Logout</button>
        </div>
      </header>

      <div className="editor-layout">
        {/* Left: Thumbnails */}
        <aside className="editor-sidebar">
          <SlideThumbnailList
            deck={deck}
            deckId={deckId}
            previewImages={previewImages}
            activeSlide={activeSlide}
            onSelect={setActiveSlide}
          />
        </aside>

        {/* Center: Main Preview */}
        <section className="editor-preview">
          <DeckPreview
            deck={deck}
            deckId={deckId}
            previewImages={previewImages}
            activeSlide={activeSlide}
          />
        </section>

        {/* Right: Chat */}
        <aside className="editor-chat">
          <ChatPanel deckId={deckId} onDeckUpdated={handleDeckUpdated} />
        </aside>
      </div>

      {error && <p className="error-message">⚠ {error}</p>}
    </div>
  );
}

export default App;
